import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/base/Navigation';
import Footer from '../../components/feature/Footer';

// Animation Hook
const useScrollAnimation = () => {
  const [visibleElements, setVisibleElements] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return visibleElements;
};

export default function Home() {
  const visibleElements = useScrollAnimation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [nextImageIndex, setNextImageIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Enhanced background images array with professional office and tech images
  const backgroundImages = [
    'https://public.readdy.ai/ai/img_res/2fe6d41aefe0d7cb7e2e8f1009bf2e4c.jpg',
    'https://readdy.ai/api/search-image?query=Bright%20modern%20coworking%20space%20with%20entrepreneurs%20working%20on%20laptops%20at%20wooden%20communal%20tables%20exposed%20brick%20walls%20industrial%20pendant%20lighting%20large%20windows%20with%20natural%20light%20real%20candid%20business%20photography%20authentic%20startup%20environment%20professional%20workspace%20with%20coffee%20cups%20and%20notebooks&width=1920&height=1080&seq=hero-prof-cowork-2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Luxury%20corner%20office%20with%20panoramic%20city%20views%20through%20floor%20to%20ceiling%20glass%20windows%20polished%20mahogany%20desk%20with%20laptop%20and%20documents%20black%20leather%20executive%20chair%20golden%20hour%20sunlight%20real%20estate%20photography%20high%20end%20business%20interior%20professional%20corporate%20environment&width=1920&height=1080&seq=hero-prof-exec-3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Modern%20conference%20room%20with%20long%20polished%20table%20and%20leather%20chairs%20large%20flat%20screen%20display%20on%20wall%20floor%20to%20ceiling%20windows%20with%20city%20view%20natural%20daylight%20professional%20meeting%20space%20real%20architectural%20photography%20corporate%20boardroom%20authentic%20business%20interior&width=1920&height=1080&seq=hero-prof-conf-5&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Elegant%20office%20lobby%20with%20marble%20reception%20desk%20contemporary%20seating%20area%20with%20designer%20furniture%20large%20windows%20with%20natural%20light%20indoor%20tropical%20plants%20modern%20lighting%20fixtures%20real%20commercial%20interior%20photography%20welcoming%20professional%20entrance%20high%20end%20corporate%20space&width=1920&height=1080&seq=hero-prof-lobby-6&orientation=landscape'
  ];

  // Dynamic rotation with varying intervals and unique transition effects
  useEffect(() => {
    const getRandomInterval = () => {
      // Random interval between 4-7 seconds for more organic feel
      return Math.floor(Math.random() * 3000) + 4000;
    };

    const rotateImages = () => {
      setIsTransitioning(true);
      
      setTimeout(() => {
        setCurrentImageIndex(nextImageIndex);
        setNextImageIndex((nextImageIndex + 1) % backgroundImages.length);
        setIsTransitioning(false);
      }, 800);
    };

    const interval = setInterval(rotateImages, getRandomInterval());

    return () => clearInterval(interval);
  }, [nextImageIndex, backgroundImages.length]);

  const getAnimationClass = (elementId: string, baseClass: string = '') => {
    const isVisible = visibleElements.has(elementId);
    return `${baseClass} transition-all duration-1000 ease-out ${
      isVisible
        ? 'opacity-100 translate-y-0 scale-100'
        : 'opacity-0 translate-y-8 scale-95'
    }`;
  };

  const features = [
    {
      icon: 'ri-palette-line',
      title: 'Custom Design',
      description: 'We create a custom design tailored to your brand and industry, ensuring your website stands out and reflects your unique identity.'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Mobile Responsive',
      description: 'Your website will look great and function perfectly on all devices, from desktop computers to smartphones and tablets.'
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Easy Management',
      description: 'Simple content management system that makes it easy to update your website without technical knowledge or coding skills.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Secure & Reliable',
      description: 'Advanced security features and reliable hosting ensure your website stays safe, fast, and always accessible to your customers.'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Ongoing Support',
      description: 'Dedicated support team available to help you with updates, questions, and any technical assistance you may need.'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Fast Performance',
      description: 'Optimized for speed and performance to provide the best user experience and keep your visitors engaged.'
    }
  ];

  const process = [
    {
      number: '01',
      title: 'Discovery',
      description: 'We start by understanding your business goals, target audience, and design preferences through a detailed consultation.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20business%20consultation%20meeting%20in%20modern%20conference%20room%20two%20executives%20discussing%20strategy%20over%20laptop%20and%20documents%20on%20polished%20wooden%20table%20natural%20daylight%20from%20large%20windows%20warm%20professional%20atmosphere%20authentic%20corporate%20photography%20realistic%20business%20interaction%20high%20quality%20commercial%20photography%20professional%20handshake%20and%20collaboration&width=600&height=400&seq=process-discovery-meeting-v2&orientation=landscape'
    },
    {
      number: '02',
      title: 'Design',
      description: 'Our AI technology creates a custom website design that matches your brand and meets your specific business needs.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20UX%20UI%20designer%20working%20at%20pristine%20Apple%20iMac%20displaying%20beautiful%20website%20homepage%20mockup%20with%20clear%20English%20text%20headings%20navigation%20menu%20hero%20section%20and%20call%20to%20action%20buttons%20visible%20Adobe%20XD%20Figma%20artboards%20open%20on%20secondary%20monitor%20showing%20wireframes%20and%20color%20palette%20swatches%20organized%20modern%20desk%20with%20Wacom%20tablet%20wireless%20keyboard%20mouse%20and%20coffee%20cup%20bright%20contemporary%20office%20with%20floor%20to%20ceiling%20windows%20natural%20daylight%20streaming%20in%20ultra%20sharp%20focus%20crystal%20clear%208K%20professional%20photography%20highest%20quality%20realistic%20workplace%20environment&width=600&height=400&seq=process-webdesign-english-v3&orientation=landscape'
    },
    {
      number: '03',
      title: 'Development',
      description: 'We build your website with clean code, fast performance, and all the features you need to succeed online.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20software%20engineer%20coding%20at%20modern%20workstation%20with%20three%20large%20monitors%20displaying%20colorful%20programming%20code%20syntax%20HTML%20CSS%20JavaScript%20React%20framework%20visible%20clean%20organized%20desk%20setup%20with%20mechanical%20keyboard%20coffee%20mug%20and%20notepad%20bright%20modern%20office%20with%20natural%20window%20lighting%20authentic%20workplace%20photography%20real%20developer%20environment%20high%20quality%20professional%20photography%20sharp%20focus%20realistic%20details&width=600&height=400&seq=process-prof-dev-v2&orientation=landscape'
    },
    {
      number: '04',
      title: 'Launch',
      description: 'After thorough testing and your approval, we launch your website and provide training on how to manage it.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20business%20team%20celebrating%20successful%20project%20launch%20gathered%20around%20large%20monitor%20displaying%20ClearPath%20Intelligence%20website%20homepage%20and%20logo%20modern%20bright%20office%20with%20floor%20to%20ceiling%20windows%20natural%20daylight%20people%20smiling%20and%20pointing%20at%20screen%20authentic%20candid%20workplace%20photography%20genuine%20celebration%20moment%20real%20corporate%20environment%20high%20quality%20commercial%20photography&width=600&height=400&seq=process-prof-launch&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Navigation transparent />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src={backgroundImages[currentImageIndex]}
            alt="Modern office workspace"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center w-full">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 font-space leading-tight">
            Professional Websites<br /><span className="text-indigo-400">Powered by AI</span>
          </h1>
          <p className="text-xl md:text-2xl text-indigo-400 mb-4 max-w-3xl mx-auto leading-relaxed font-semibold">
            Your Clear Path to Digital Success
          </p>
          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-3xl mx-auto leading-relaxed">
            Transform your business with intelligent web design that drives results
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link 
              to="/services"
              className="px-8 py-4 bg-indigo-500 text-white rounded-lg font-semibold hover:bg-indigo-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              View Our Services
            </Link>
            <Link 
              to="/contact"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap cursor-pointer"
            >
              Get Started Today
            </Link>
          </div>
        </div>

        {/* Image Navigation Dots */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
          {backgroundImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentImageIndex ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="features-header" 
            data-animate 
            className={getAnimationClass('features-header', 'text-center mb-16')}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
              Why Choose <span className="text-indigo-400">ClearPath Intelligence</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              We combine advanced technology with personalized service to deliver websites that help your business succeed.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                id={`feature-${index}`}
                data-animate 
                className={getAnimationClass(`feature-${index}`, 'bg-slate-700/50 rounded-xl p-8 hover:bg-slate-700/70 transition-all duration-300 border border-slate-600/50')}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-indigo-400 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">{feature.title}</h3>
                <p className="text-slate-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="process-header" 
            data-animate 
            className={getAnimationClass('process-header', 'text-center mb-16')}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-space">
              Our <span className="text-indigo-400">Process</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              From initial consultation to launch, we guide you through every step of creating your perfect website.
            </p>
          </div>

          <div className="space-y-16">
            {process.map((step, index) => (
              <div 
                key={index}
                id={`process-${index}`}
                data-animate 
                className={getAnimationClass(`process-${index}`, `grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="flex items-center mb-6">
                    <h3 className="text-3xl font-bold text-white">{step.title}</h3>
                  </div>
                  <p className="text-lg text-slate-300 leading-relaxed">{step.description}</p>
                </div>
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="relative rounded-xl overflow-hidden shadow-2xl">
                    <img 
                      src={step.image} 
                      alt={step.title}
                      className="w-full h-80 object-cover"
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.classList.add('bg-slate-700', 'flex', 'items-center', 'justify-center', 'h-80');
                          const icon = document.createElement('i');
                          icon.className = 'ri-image-line text-slate-500 text-6xl';
                          parent.appendChild(icon);
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}